import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { ScanMedia } from 'features/scan-media'

import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'

import { IScanDocument, ScanDocument } from 'shared/api/scan-document'
import { TYPE_AGE } from 'shared/constants'
import { getErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'

import {
  DialogScanPassport,
  DialogScanPassportError,
  ScanPage,
} from './components'
import s from './styles.module.scss'

const ScanPassport = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const { openDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState<boolean>(false)
  const [showCamera, setShowCamera] = useState<boolean>(false)

  const [searchParams] = useSearchParams()

  const indexPassenger = searchParams.get('passenger')

  const agePassenger = searchParams.get('age') as TYPE_AGE

  const openCamera = async () => {
    setShowCamera(true)
  }

  const makePhoto = async (photo?: File) => {
    if (!photo) {
      onShowError()

      return
    }

    setLoading(true)
    setShowCamera(false)

    try {
      const formData = new FormData()

      formData.append('file', photo)

      const { data: scanResult } = await ScanDocument.scanUserDocument(formData)

      openDialog({
        isDialog: false,
        child: (
          <DialogScanPassport
            data={scanResult.data}
            onTryAgainScan={retryCamera}
            onContinue={() => onSubmitScanDocument(scanResult.data)}
          />
        ),
      })
    } catch (error) {
      const errorText = getErrorMessage(error)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onShowError = (errorText?: string) => {
    openDialog({
      isDialog: false,
      child: (
        <DialogScanPassportError
          text={errorText || t('scan_passport.error_scaning_password')}
        />
      ),
    })
  }

  const onSubmitScanDocument = (data: IScanDocument) => {
    if (!indexPassenger || !agePassenger) return null

    formPassengersStore.setPassengerData({
      index: Number(indexPassenger),
      age: agePassenger,
      scannedData: data,
    })

    navigate(-1)
  }

  const onErrorShowCamera = () => {
    setShowCamera(false)

    openDialog({
      isDialog: false,
      child: <DialogScanPassportError text={t('scan_passport.error_open')} />,
    })
  }

  const retryCamera = () => {
    setShowCamera(true)
  }

  const onGoTundukScreen = () => {
    navigate(
      {
        pathname: RoutingPaths.avia.scanTunduk.path(),
        search: createSearchParams({
          passenger: `${indexPassenger || ''}`,
          age: `${agePassenger || ''}`,
        }).toString(),
      },
      {
        replace: true,
      },
    )
  }

  return (
    <>
      {showCamera && (
        <ScanMedia
          onMakeShot={makePhoto}
          onError={onErrorShowCamera}
          mask={<div className={s.crop} />}
          onLoadFileFromTunduk={onGoTundukScreen}
        />
      )}

      <div className="container">
        <div className={s.header}>
          <HeaderGoBack
            title={t('scan_passport.how_to_scan')}
            buttonWhite
          />
        </div>

        {loading ? (
          <Loader marginTop={150} />
        ) : (
          <ScanPage openCamera={openCamera} />
        )}
      </div>
    </>
  )
}

export default ScanPassport
